'use client'

import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import Button from '../../UiElements/Button'

import Form, { FormGeneratorHandles } from '@/components/Form'
import createSection2dFormItems from '../forms/section2dFormItems'
import DeleteAbsoluteButton from '@/components/UiElements/DeleteAbsoluteButton'
import Alert from '@/components/UiElements/Alert'

interface Section2dFormProps {
	section?: any
	isOpen?: boolean
	close: () => void
	onUpdate: (section: any) => void
	sectionData: any
	sectionList: any[]
	profileType?: string
}


const Section2dForm: React.FC<Section2dFormProps> = ({ section = {}, isOpen, close, onUpdate, sectionData = {}, profileType }) => {

	const formRef = React.useRef<FormGeneratorHandles>(null)
	const lastProcessedDataRef = React.useRef<string>('')
	const onUpdateRef = useRef(onUpdate)

	onUpdateRef.current = onUpdate

	const handleChangeSection = async (formData: Record<string, any>) => {
		const fd: Record<string, any> = { ...formData, thumbnail: null }
		const shapeType = fd.shapeType?.value || fd.shapeType || 'rectangle'

		// For circle, sync radius -> width/height
		if (shapeType === 'circle' && fd.radius) {
			const r = Number(fd.radius)
			fd.width = (r * 2).toString()
			fd.height = (r * 2).toString()
		}

		// Mark this data as processed so the useEffect doesn't re-trigger
		lastProcessedDataRef.current = JSON.stringify(fd)

		const validations = await formRef.current?.validate(true, fd)
		onUpdate({
			formData: fd,
			validations,
		})
	}

	const handleUpdateForm = (data: any) => {
		// sectionData is an external parent/canvas update. It should only hydrate
		// this form; emitting it back through onUpdate creates an async feedback
		// loop that can overwrite a shape completed while validation is pending.
		formRef.current?.setData(data)
	}

	const handleDeleteShape = useCallback(async () => {
		const currentFormData = formRef.current?.getData() || {}
		const nextFormData = {
			...currentFormData,
			offsetX: null,
			offsetY: null,
			width: null,
			height: null,
			radius: null,
			vertices: null,
			rotation: '0',
			thumbnail: null,
		}

		formRef.current?.setData(nextFormData)
		lastProcessedDataRef.current = JSON.stringify(nextFormData)

		const validations = await formRef.current?.validate(true, nextFormData)
		onUpdateRef.current({
			formData: nextFormData,
			validations,
			isShapeDeleted: true,
		})
	}, [])

	const formItems = useMemo(
		() => createSection2dFormItems({ onDeleteShape: handleDeleteShape, profileType }),
		[handleDeleteShape, profileType]
	)

	const handleDeleteSection = async () => {
		const form = formRef.current?.getData()
		Alert.showAlert({
			title: 'Delete Section',
			isCancelable: true,
			danger: true,
			button: 'Delete!',
			message:
				<div className={'flex flex-col items-center justify-center '}>
					Are you sure you want to delete the section &quot;{form?.name}&quot;?
				</div>,
			callBack: async (ok) => {
				if (ok) {
					onUpdate({
						formData: form,
						validations: [],
						isDeleted: true
					})
				}
			},
		})
	}

	useEffect(() => {
		if (sectionData) {
			const dataKey = JSON.stringify(sectionData)
			if (dataKey !== lastProcessedDataRef.current) {
				lastProcessedDataRef.current = dataKey
				handleUpdateForm(sectionData)
			}
		}
	}, [sectionData])

	useEffect(() => {
		formRef.current?.setData({})
	}, [])


	return (
		<div className={`bg-white rounded-md  ${isOpen ? ' !w-[350px] !h-auto' : '!w-[0px] !h-0'}  flex flex-col gap-md relative overflow-hidden`}>
			<div className={'p-md'}>
				<div className={' flex flex-row justify-center item-center h-[27px] '}>
					<div className={'flex flex-grow item-center text-title-lg capitalize'}>
						Section
					</div>

					<Button
						onClick={close}
						className={
							'font-icon-solid !w-[37px] !h-[37px] overflow-hidden !items-center !justify-center flex text-[18px] !p-0 relative top-[-5px] right-[-10px]'
						}
						type={'link'}
					>

					</Button>
				</div>
			</div>

			<div className={` transition-all  relative duration-500 max-h-calc(100vh-350px)`}>
				<div className="flex items-start gap-[30px]  flex-row transition-all duration-500 ">

					<div className={'w-[350px] px-md'}>
						<div className={'grid grid-cols-12 gap-md !w-[100%] pb-[20px] '}>
							<Form
								ref={formRef}
								items={formItems}
								defaultValues={section}
								onChange={handleChangeSection}
								liveValidate
							/>
							<DeleteAbsoluteButton
								label={'Delete Section'}
								className={'!relative !w-full !h-[45px] px-xl col-span-12'}
								onClick={handleDeleteSection}
							/>
						</div>
					</div>


				</div>
			</div>
		</div>
	)
}

export default Section2dForm
